import type { ETSConfig } from "./config.js";
import { ETS } from "./ets.js";

/** Shape of the stub object created by the inline snippet. */
export interface ETSStub {
  _k: string;
  _h: string;
  _t: { t: number; o: number; r: string; u: string };
  _q: Array<[string, unknown[]]>;
  /** URL of the full SDK bundle; set by the loader (Task 3) once resolved. */
  _src?: string;
  [method: string]: unknown;
}

declare global {
  interface Window {
    ets?: ETSStub | ETS;
    /**
     * Expected hash published by the snippet out-of-band. Used by autoInit
     * to detect tampering where `window.ets` has been replaced between the
     * snippet executing and the SDK bundle running.
     */
    __ETS_EXPECTED_HASH__?: string;
  }
}

/** Public method names that the real ETS instance exposes. */
type ETSMethod = "track" | "page" | "identify" | "group" | "alias" | "flush" | "ready" | "reset";

const PUBLIC_METHODS: ETSMethod[] = [
  "track",
  "page",
  "identify",
  "group",
  "alias",
  "flush",
  "ready",
  "reset",
];

function isStub(value: unknown): value is ETSStub {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj._k === "string" && Array.isArray(obj._q);
}

export interface AutoInitOptions extends Partial<ETSConfig> {
  /**
   * Expected hash emitted by the snippet via `window.__ETS_EXPECTED_HASH__`.
   * If omitted, autoInit reads the global. When the stub's `_h` does not
   * match the expected hash, the queue is discarded and
   * `ets:tamper-detected` is dispatched on `window`.
   */
  expectedHash?: string;
}

function emitTamperEvent(reason: string, details: Record<string, unknown>): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(
      new CustomEvent("ets:tamper-detected", {
        detail: { reason, ...details },
      }),
    );
  } catch {
    // CustomEvent unsupported — swallow.
  }
}

/**
 * Detect the stub created by the inline snippet on `window.ets`,
 * replace it with a real ETS instance, and drain the queued calls.
 *
 * Security (#29): the snippet emits a single-use hash at
 * `window.__ETS_EXPECTED_HASH__`. The snippet-created stub mirrors the hash
 * on `stub._h`. If those values disagree, autoInit aborts without draining
 * the queue and dispatches `ets:tamper-detected` so observers can alert.
 * This defeats naive attacks that only overwrite `window.ets` after the
 * snippet ran.
 *
 * Page calls are drained first so the initial pageview is always recorded
 * before any track/identify/group/alias calls.
 */
export function autoInit(options?: AutoInitOptions): ETS | undefined {
  if (typeof window === "undefined") return undefined;

  // Capture the expected hash into a closure before we touch window.ets, so a
  // later mutation of `window.__ETS_EXPECTED_HASH__` cannot retroactively
  // validate a tampered stub.
  const expected =
    options?.expectedHash !== undefined
      ? options.expectedHash
      : window.__ETS_EXPECTED_HASH__;

  const stub = window.ets;

  if (!isStub(stub)) {
    return undefined;
  }

  if (typeof expected === "string" && expected.length > 0) {
    if (stub._h !== expected) {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("[ets] tamper detected: _h mismatch — aborting autoInit");
      }
      emitTamperEvent("hash_mismatch", {
        expected,
        actual: typeof stub._h === "string" ? stub._h : null,
      });
      return undefined;
    }
  }

  const writeKey = stub._k;
  const queue = [...stub._q];

  const etsConfig: Partial<ETSConfig> = { ...(options ?? {}) };
  delete (etsConfig as { expectedHash?: string }).expectedHash;

  const instance = new ETS({
    writeKey,
    ...etsConfig,
  });

  // Replace window.ets with the real instance
  window.ets = instance as unknown as Window["ets"];

  // Drain the queue: page calls first, then everything else (preserving relative order within each group)
  const pageCalls = queue.filter(([method]) => method === "page");
  const otherCalls = queue.filter(([method]) => method !== "page");

  for (const [method, args] of [...pageCalls, ...otherCalls]) {
    if (PUBLIC_METHODS.includes(method as ETSMethod)) {
      const fn = (instance as unknown as Record<string, unknown>)[method];
      if (typeof fn === "function") {
        fn.apply(instance, args);
      }
    }
  }

  return instance;
}
